import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_OPERADORES } from './mockDataEjemplos';
import { conversionEquipo } from './calculosReportes';

/**
 * Dispersión carga vs. conversión — un punto por operador. Cruz de cuadrantes en los
 * promedios del equipo. Las etiquetas se dibujan como overlay posicionado (no un
 * scatter de Chart.js) para poder evitar que se pisen entre sí.
 * @param {{data: Array<{idOperador: number, operador: string, totalAtendidos: number, tasaConversion: number}>}} props
 */
const CargaVsConversion = ({ data, compacto = false }) => {
    const puntos = useMemo(() => (data || []).filter((f) => f.idOperador != null && f.totalAtendidos > 0), [data]);

    const { promedioX, promedioY, minX, maxX, minY, maxY } = useMemo(() => {
        if (!puntos.length) return { promedioX: 0, promedioY: 0, minX: 0, maxX: 1, minY: 0, maxY: 1 };
        const xs = puntos.map((p) => p.totalAtendidos);
        const ys = puntos.map((p) => Number(p.tasaConversion));
        return {
            promedioX: xs.reduce((a, b) => a + b, 0) / xs.length,
            // La conversión del equipo es la ponderada (la misma de la tabla de operadores y del encabezado).
            promedioY: conversionEquipo(puntos) ?? ys.reduce((a, b) => a + b, 0) / ys.length,
            minX: 0, maxX: Math.max(...xs) * 1.15,
            minY: 0, maxY: Math.max(...ys) * 1.25,
        };
    }, [puntos]);

    // Padding del 10% en cada borde: sin esto, los puntos extremos quedan pegados
    // al marco del gráfico y se recortan/superponen con la cruz de promedios.
    const PADDING = 10;
    const posX = (v) => PADDING + ((v - minX) / (maxX - minX || 1)) * (100 - PADDING * 2);
    const posY = (v) => (100 - PADDING) - ((v - minY) / (maxY - minY || 1)) * (100 - PADDING * 2);

    // Evita que dos etiquetas cercanas se pisen: si otro punto está a menos de
    // ~14% en x y ~10% en y, la etiqueta va debajo en vez de arriba.
    const posiciones = puntos.map((p) => ({ x: posX(p.totalAtendidos), y: posY(Number(p.tasaConversion)) }));
    const etiquetaAbajo = puntos.map((p, i) => posiciones.some((otro, j) => j !== i && Math.abs(otro.x - posiciones[i].x) < 14 && Math.abs(otro.y - posiciones[i].y) < 10 && j < i));

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Carga vs. conversión"
                        queMuestra="Te dice si los operadores que más turnos atienden son también los que más convierten, o si carga alta y buena conversión son en realidad cosas independientes."
                        ejemploVisual={<CargaVsConversion compacto data={MOCK_OPERADORES} />}
                        comoLeerlo={[
                            'Cada punto es un operador; su posición horizontal es cuántos turnos atendió y la vertical, su % de conversión.',
                            'Las líneas punteadas cruzadas marcan el promedio del equipo en carga (vertical) y en conversión (horizontal) — dividen el cuadro en 4 cuadrantes.',
                            'Verde = conversión igual o por encima del promedio del equipo. Ámbar = por debajo — no es una alarma dura, es relativo al resto del equipo en este período.',
                        ]}
                        analisis="En este ejemplo los puntos verdes (mejor conversión) están del lado de MENOS turnos atendidos, y el punto con más carga (Rosario, a la derecha) es ámbar — sugiere que a mayor volumen de turnos, a este equipo le está costando sostener la calidad de la conversión. Si el patrón se repite en datos reales, antes de repartir turnos por igual conviene preguntarse si hay un techo de carga por operador a partir del cual la conversión empieza a resentirse."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Carga vs. conversión</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '10px' }}>
                        Turnos atendidos vs. % de conversión a trámite, por operador.
                    </Typography>
                </>
            )}
            {puntos.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>No hay datos suficientes por operador en el período.</Typography>
            ) : (
                <>
                    {!compacto && (
                        <Box sx={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: COLOR.verde }} />
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>Conversión ≥ promedio del equipo</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: COLOR.ambar }} />
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 11, color: COLOR.grisTexto }}>Conversión &lt; promedio del equipo</Typography>
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ position: 'relative', height: compacto ? 160 : 280, border: `1px solid ${COLOR.gris}`, borderRadius: '8px' }}>
                        <Box sx={{ position: 'absolute', left: `${posX(promedioX)}%`, top: 0, bottom: 0, width: '1px', background: COLOR.grisFrio, borderLeft: `1px dashed ${COLOR.grisFrio}` }} />
                        <Box sx={{ position: 'absolute', top: `${posY(promedioY)}%`, left: 0, right: 0, height: '1px', borderTop: `1px dashed ${COLOR.grisFrio}` }} />
                        {puntos.map((p, i) => {
                            const buena = Number(p.tasaConversion) >= promedioY;
                            const abajo = etiquetaAbajo[i];
                            return (
                                <Box key={p.idOperador} sx={{ position: 'absolute', left: `${posiciones[i].x}%`, top: `${posiciones[i].y}%`, transform: 'translate(-50%, -50%)' }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: buena ? COLOR.verde : COLOR.ambar, border: '2px solid #fff', margin: '0 auto' }} />
                                    <Typography sx={{
                                        position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                                        top: abajo ? 12 : undefined, bottom: abajo ? undefined : 12,
                                        fontFamily: FUENTE, fontSize: 10.5, color: COLOR.negro, whiteSpace: 'nowrap',
                                    }}>
                                        {p.operador}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Card>
    );
};

export default CargaVsConversion;
